import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Col, Form, Row } from "antd";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { DEFAULT_LANGUAGE, EUserRole, ServiceTemplateField, TRANSLATABLE_SERVICE_TEMPLATE_ATTRIBUTES_LIST, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { useServiceCdnState } from "../../../../../../hooks/service/useServiceCdnState.js";
import { useTranslation } from "../../../../../../others/i18n.js";
import { useStores } from "../../../../../../store/root.js";
import { defaultTranslationFlexNamePath, isExtendedFieldHidden } from "../../../../../../util/template/form.js";
import { getDisableStrategies } from "../../../../../../util/template/merge-strategies.js";
import { getFieldNameTranslated } from "../../../../../../util/template/translation.js";
import { getQuillEditorWrapperPropsDefaultLang, hasTranslationFlagsChanged } from "../../../../../../util/translation.js";
import { QuillEditorFormField } from "../../../../../third-party/quill-editor-form.js";
import { QuillSimpleHtmlContent } from "../../../../../third-party/quill-simple-html-content.js";
import { LanguageIcon } from "../../fields/translation/language-icon-addon/language-icon.js";
import { TranslationQuillFormFields } from "../../fields/translation/language-quill-input/language-quill-form-fields.js";
import { FormFieldVisibilityWrapper } from "../../formFieldVisibilityWrapper.js";
import { TemplateMetaDataExtendsStrategyFormField } from "../../templateMetaDataExtendsStrategyFormField.js";
/**
 * Form field for the SCC conclusion instructions notice.
 * This field is only visible if the CDN is enabled.
 *
 * @returns The form field.
 */ const FormFieldServiceTemplateSccConclusionInstructionsNotice = observer(()=>{
    const { t } = useTranslation();
    const { rcbAuthenticationStore, rcbUserSettingsStore: { settings: { translationSettings } } } = useStores();
    const { isTranslationMode } = translationSettings;
    // Triggers form item re-render on translation mode toggle
    const lastKnownTranslationMode = isTranslationMode;
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    const form = Form.useFormInstance();
    const cdnFieldState = useServiceCdnState();
    const isFieldVisible = useMemo(()=>[
            "enabledWithStandardClausesAndNotEmbeddingOnlyExternalResources",
            "enabledWithStandardClauses"
        ].includes(cdnFieldState), [
        cdnFieldState
    ]);
    return /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
        fieldNames: ServiceTemplateField.SccConclusionInstructionsNotice,
        permissionCheck: {
            whenRole: EUserRole.Translator,
            roleWhiteList: TRANSLATABLE_SERVICE_TEMPLATE_ATTRIBUTES_LIST
        },
        forceHidden: !isFieldVisible,
        forceHiddenFormCallback: isExtendedFieldHidden(ServiceTemplateField.SccConclusionInstructionsNotice, hasCreatorRole),
        children: /*#__PURE__*/ _jsx(Form.Item, {
            label: getFieldNameTranslated(ServiceTemplateField.SccConclusionInstructionsNotice, t),
            required: isFieldVisible,
            rules: isFieldVisible ? [
                {
                    required: true,
                    message: t("Please provide instructions for concluding SCCs.")
                }
            ] : [],
            shouldUpdate: (prev, next)=>prev.extends?.identifier !== next.extends?.identifier || hasTranslationFlagsChanged(prev, next, ServiceTemplateField.SccConclusionInstructionsNotice) || lastKnownTranslationMode !== isTranslationMode,
            hasFeedback: true,
            children: ({ getFieldValue })=>{
                const extended = getFieldValue(TemplateMetaDataField.Extends);
                return /*#__PURE__*/ _jsxs(TemplateMetaDataExtendsStrategyFormField, {
                    extendsFieldName: ServiceTemplateField.SccConclusionInstructionsNotice,
                    disableStrategies: getDisableStrategies(),
                    onKeepContent: extended && /*#__PURE__*/ _jsx(QuillSimpleHtmlContent, {
                        value: extended[ServiceTemplateField.SccConclusionInstructionsNotice],
                        readOnly: true
                    }),
                    children: [
                        /*#__PURE__*/ _jsxs(Row, {
                            gutter: [
                                10,
                                10
                            ],
                            style: {
                                flexFlow: "row"
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Col, {
                                    flex: 1,
                                    children: /*#__PURE__*/ _jsx(Form.Item, {
                                        name: ServiceTemplateField.SccConclusionInstructionsNotice,
                                        noStyle: true,
                                        rules: isFieldVisible ? [
                                            {
                                                required: true,
                                                message: t("Please provide SCC conclusion instructions notice. These ensure compliance and proper setup when using a Content Delivery Network.")
                                            }
                                        ] : [],
                                        children: /*#__PURE__*/ _jsx(QuillEditorFormField, {
                                            namePath: ServiceTemplateField.SccConclusionInstructionsNotice,
                                            quillEditorProps: {
                                                readOnly: !hasCreatorRole,
                                                ...getQuillEditorWrapperPropsDefaultLang(ServiceTemplateField.SccConclusionInstructionsNotice, form)
                                            }
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Col, {
                                    flex: "0",
                                    children: /*#__PURE__*/ _jsx(LanguageIcon, {
                                        lang: DEFAULT_LANGUAGE,
                                        flexPathConfig: {
                                            absolute: {
                                                namePath: ServiceTemplateField.SccConclusionInstructionsNotice,
                                                translationNamePath: defaultTranslationFlexNamePath(ServiceTemplateField.SccConclusionInstructionsNotice)
                                            }
                                        }
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(TranslationQuillFormFields, {
                            flexPathConfig: {
                                absolute: {
                                    namePath: defaultTranslationFlexNamePath(ServiceTemplateField.SccConclusionInstructionsNotice)
                                }
                            }
                        })
                    ]
                });
            }
        })
    });
});
export { FormFieldServiceTemplateSccConclusionInstructionsNotice };
